import React, { useEffect, useState } from "react";
import { api, wsUrl } from "@/lib/api";
import {
  Cpu, HardDrive, Activity, AudioLines, ShieldCheck, ShieldAlert, ShieldX, FileDown, Database,
} from "lucide-react";
import { toast } from "sonner";
import R2IntegrityTool from "@/components/R2IntegrityTool";

function pct(x, denom = 100) {
  const v = Math.max(0, Math.min(1, x / denom));
  return `${(v * 100).toFixed(0)}%`;
}

/**
 * Loudness Snapshot — one live tile per channel showing delta from LKFS
 * target with a Green (within tolerance), Amber (near edge), or Red (out of
 * tolerance) state, updated from the shared /api/ws/channels ticker.
 */
function LoudnessSnapshot({ channels }) {
  const [live, setLive] = useState({});

  useEffect(() => {
    const ws = new WebSocket(wsUrl("/api/ws/channels"));
    ws.onmessage = (e) => {
      try {
        const t = JSON.parse(e.data);
        setLive(Object.fromEntries((t.channels || []).map((c) => [c.id, c])));
      } catch { /* noop */ }
    };
    return () => { try { ws.close(); } catch { /* noop */ } };
  }, []);

  const stateOf = (delta, tol) => {
    const a = Math.abs(delta);
    if (a <= tol * 0.6) return "green";
    if (a <= tol) return "amber";
    return "red";
  };
  const stateColor = {
    green: { bg: "bg-emerald-500/10", border: "border-emerald-500/40", text: "text-emerald-400", Icon: ShieldCheck },
    amber: { bg: "bg-amber-500/10", border: "border-amber-500/40", text: "text-amber-400", Icon: ShieldAlert },
    red:   { bg: "bg-red-500/10",   border: "border-red-500/50",   text: "text-red-400",   Icon: ShieldX },
  };

  return (
    <div className="panel" data-testid="loudness-snapshot">
      <div className="panel-header">
        <span className="panel-title">Loudness snapshot · live</span>
        <span className="font-mono text-[10px] text-zinc-500 hidden sm:inline">Δ LKFS vs target · CALM Act</span>
      </div>
      <div className="p-3 grid grid-cols-2 md:grid-cols-2 xl:grid-cols-4 gap-2">
        {channels.filter((c) => (c.cdn?.publish_state) === "live").map((c) => {
          const l = live[c.id];
          const target = c.audio_profile?.loudness_target_lkfs ?? -24;
          const tol = c.audio_profile?.loudness_tolerance_lu ?? 2;
          const spec = c.audio_profile?.loudness_spec ?? "ATSC A/85";
          const layout = c.audio_profile?.primary_layout ?? "5.1";
          const currentLkfs = l?.audio_lufs ?? target;
          const delta = currentLkfs - target;
          const state = l ? stateOf(delta, tol) : "green";
          const s = stateColor[state];
          const arrow = delta > 0 ? "▲" : delta < 0 ? "▼" : "◆";
          return (
            <div
              key={c.id}
              data-testid={`loudness-tile-${c.slug}`}
              className={`rounded-sm border ${s.border} ${s.bg} p-3 flex flex-col gap-1`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-sm" style={{ background: c.color }} />
                  <span className="font-display font-bold text-sm truncate">{c.name}</span>
                </div>
                <s.Icon className={`w-4 h-4 ${s.text}`} strokeWidth={2} />
              </div>
              <div className="flex items-baseline gap-2">
                <div className={`font-display font-black text-2xl ${s.text}`} data-testid={`loudness-delta-${c.slug}`}>
                  {arrow} {Math.abs(delta).toFixed(1)}
                </div>
                <div className="font-mono text-[10px] text-zinc-500">LU</div>
              </div>
              <div className="font-mono text-[10px] text-zinc-400" data-testid={`loudness-current-${c.slug}`}>
                {currentLkfs.toFixed(1)} LKFS
                <span className="text-zinc-600"> / </span>
                target {target} · tol ±{tol}
              </div>
              <div className="font-mono text-[9px] text-zinc-500 uppercase tracking-widest">
                {layout} · {spec}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * 60-minute LUFS-over-hour compliance report per channel.
 * Renders a bar sparkline where each bar = 1 minute; bars beyond the
 * ±tolerance band from target are amber (near edge) or red (out).
 */
function LoudnessComplianceReport({ minutes = 60 }) {
  const [data, setData] = useState(null);
  const [range, setRange] = useState(60);

  useEffect(() => {
    let alive = true;
    const load = () => api.get(`/monitoring/loudness_history?minutes=${range}`).then((r) => alive && setData(r.data));
    load();
    const iv = setInterval(load, 20000);
    return () => { alive = false; clearInterval(iv); };
  }, [range]);

  if (!data) return <div className="panel p-4 text-xs text-zinc-500 italic">Loading history…</div>;

  const overallCompliance = data.channels.length
    ? (data.channels.reduce((a, c) => a + c.compliance_pct, 0) / data.channels.length).toFixed(1)
    : "—";

  return (
    <div className="panel" data-testid="loudness-compliance-report">
      <div className="panel-header">
        <span className="panel-title">LUFS-over-hour compliance</span>
        <div className="flex items-center gap-2 font-mono text-[10px]">
          <span className="text-zinc-500">
            AVG compliance <span className="text-emerald-400">{overallCompliance}%</span>
          </span>
          <select
            value={range}
            onChange={(e) => setRange(Number(e.target.value))}
            data-testid="loudness-range-select"
            className="bg-black border border-zinc-800 rounded-sm px-1 py-0.5 text-[10px]"
          >
            <option value={15}>15m</option>
            <option value={60}>60m</option>
            <option value={180}>3h</option>
            <option value={720}>12h</option>
          </select>
        </div>
      </div>
      <div className="p-3 space-y-3">
        {data.channels.map((c) => {
          const w = c.samples.length;
          const lo = c.target_lkfs - c.tolerance_lu;
          const hi = c.target_lkfs + c.tolerance_lu;
          // Bar sparkline
          const view = { min: c.target_lkfs - 6, max: c.target_lkfs + 6 };
          return (
            <div
              key={c.channel_id}
              className="border border-zinc-800 rounded-sm p-2 bg-black/30"
              data-testid={`compliance-row-${c.slug}`}
            >
              <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: c.color }} />
                  <span className="font-display font-bold text-sm truncate">{c.channel_name}</span>
                  <span className="font-mono text-[10px] text-zinc-500 truncate hidden sm:inline">
                    target {c.target_lkfs} ±{c.tolerance_lu} · {c.loudness_spec}
                  </span>
                </div>
                <div className="font-mono text-[10px] flex items-center gap-2 shrink-0">
                  <span className="text-zinc-500">avg {c.avg_lkfs}</span>
                  <span className="text-zinc-500 hidden sm:inline">min {c.min_lkfs}</span>
                  <span className="text-zinc-500 hidden sm:inline">max {c.max_lkfs}</span>
                  <span
                    className={`px-1.5 py-0.5 rounded-sm font-bold ${
                      c.compliance_pct >= 95
                        ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/40"
                        : c.compliance_pct >= 80
                        ? "bg-amber-500/10 text-amber-400 border border-amber-500/40"
                        : "bg-red-500/10 text-red-400 border border-red-500/40"
                    }`}
                    data-testid={`compliance-pct-${c.slug}`}
                  >
                    {c.compliance_pct}%
                  </span>
                </div>
              </div>
              <div
                className="relative h-14 bg-black/60 border border-zinc-900 rounded-sm overflow-hidden"
                title={`${w} minute-samples`}
              >
                {/* Tolerance band */}
                <div
                  className="absolute inset-x-0 bg-emerald-500/10 border-y border-emerald-500/20"
                  style={{
                    bottom: `${((lo - view.min) / (view.max - view.min)) * 100}%`,
                    height: `${((hi - lo) / (view.max - view.min)) * 100}%`,
                  }}
                />
                {/* Target line */}
                <div
                  className="absolute inset-x-0 h-px bg-emerald-400/70"
                  style={{ bottom: `${((c.target_lkfs - view.min) / (view.max - view.min)) * 100}%` }}
                />
                {/* Bars */}
                <div className="absolute inset-0 flex items-end">
                  {c.samples.map((s, i) => {
                    const outside = s.lkfs < lo || s.lkfs > hi;
                    const near = !outside && Math.abs(s.lkfs - c.target_lkfs) > c.tolerance_lu * 0.6;
                    const h = ((s.lkfs - view.min) / (view.max - view.min)) * 100;
                    return (
                      <div
                        key={i}
                        style={{ height: `${h}%`, width: `${100 / w}%` }}
                        className={`${
                          outside ? "bg-red-500/80" : near ? "bg-amber-500/80" : "bg-emerald-500/70"
                        }`}
                      />
                    );
                  })}
                </div>
              </div>
              <div className="mt-1 flex items-center justify-between font-mono text-[9px] text-zinc-500">
                <span>-{data.minutes} min</span>
                <span>now</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * R2 Integrity — operator self-heal tool (validate + re-queue with live
 * progress). Wraps the shared R2IntegrityTool in the Monitoring panel chrome.
 */
function R2IntegrityPanel() {
  return (
    <div className="panel" data-testid="r2-integrity-panel">
      <div className="panel-header">
        <span className="panel-title flex items-center gap-2">
          <Database className="w-3.5 h-3.5 text-zinc-500" /> R2 asset integrity
        </span>
        <span className="font-mono text-[10px] text-zinc-500 hidden sm:inline">HEAD-check · demote 4xx · self-heal</span>
      </div>
      <div className="p-4">
        <R2IntegrityTool />
      </div>
    </div>
  );
}

export default function Monitoring() {
  const [m, setM] = useState(null);
  const [channels, setChannels] = useState([]);

  useEffect(() => {
    const load = () => api.get("/monitoring").then((r) => setM(r.data));
    load();
    const iv = setInterval(load, 3000);
    api.get("/channels").then((r) => setChannels(r.data)).catch(() => { /* noop */ });
    return () => clearInterval(iv);
  }, []);

  if (!m) return <div className="p-6 text-zinc-500">Loading…</div>;

  const downloadPdf = async () => {
    try {
      toast.info("Generating compliance PDF…");
      const r = await api.get("/compliance/pdf", { params: { minutes: 1440 }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `moxyplay-compliance-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch {
      toast.error("PDF export failed");
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">§ Monitoring</div>
          <h1 className="font-display font-bold text-xl sm:text-2xl mt-1">System health · Loudness compliance</h1>
        </div>
        <button
          onClick={downloadPdf}
          data-testid="compliance-pdf-btn"
          className="font-mono text-[11px] uppercase tracking-widest px-3 py-2 rounded-sm border border-emerald-500/60 text-emerald-400 hover:bg-emerald-500/10 flex items-center gap-2 shrink-0"
        >
          <FileDown className="w-4 h-4" /> Compliance PDF · 24 h
        </button>
      </div>

      <div className="grid md:grid-cols-4 gap-3">
        {[
          { label: "CPU", value: m.cpu_percent, icon: Cpu, unit: "%" },
          { label: "Memory", value: m.memory_percent, icon: HardDrive, unit: "%" },
          { label: "Disk", value: m.disk_percent, icon: HardDrive, unit: "%" },
          { label: "Channels on air", value: m.channels_on_air, icon: Activity, unit: `/${m.channels_total}` },
        ].map((s) => (
          <div key={s.label} className="panel p-4" data-testid={`monitor-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{s.label}</div>
              <s.icon className="w-4 h-4 text-zinc-500" strokeWidth={1.5} />
            </div>
            <div className="font-display font-black text-3xl">
              {typeof s.value === "number" ? s.value : s.value}
              <span className="text-zinc-500 text-lg ml-1">{s.unit}</span>
            </div>
            {typeof s.value === "number" && s.unit === "%" && (
              <div className="mt-2 h-1.5 bg-zinc-900 rounded-sm overflow-hidden">
                <div
                  className="h-full transition-all"
                  style={{
                    width: `${s.value}%`,
                    background: s.value > 80 ? "#EF4444" : s.value > 60 ? "#F59E0B" : "#10B981",
                  }}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {channels.length > 0 && (
        <>
          <LoudnessSnapshot channels={channels} />
          <LoudnessComplianceReport />
        </>
      )}

      <R2IntegrityPanel />

      <div className="grid md:grid-cols-2 gap-4">
        <div className="panel">
          <div className="panel-header">
            <span className="panel-title">Network throughput</span>
            <AudioLines className="w-3.5 h-3.5 text-zinc-500" />
          </div>
          <div className="p-4 space-y-3">
            <div>
              <div className="flex justify-between text-xs mb-1 font-mono">
                <span className="text-zinc-500">INGRESS</span>
                <span className="text-emerald-400">{m.network_mbps_in.toFixed(1)} Mb/s</span>
              </div>
              <div className="h-2 bg-zinc-900 rounded-sm overflow-hidden">
                <div className="h-full bg-emerald-500" style={{ width: pct(m.network_mbps_in, 500) }} />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-xs mb-1 font-mono">
                <span className="text-zinc-500">EGRESS</span>
                <span className="text-red-400">{m.network_mbps_out.toFixed(1)} Mb/s</span>
              </div>
              <div className="h-2 bg-zinc-900 rounded-sm overflow-hidden">
                <div className="h-full bg-red-500" style={{ width: pct(m.network_mbps_out, 800) }} />
              </div>
            </div>
            <div className="pt-4 border-t border-zinc-800 text-[10px] font-mono text-zinc-500">
              UPTIME: <span className="text-emerald-400">{m.uptime_hours.toFixed(1)} h</span>
            </div>
          </div>
        </div>

        <div className="panel">
          <div className="panel-header"><span className="panel-title">Protocol connections</span></div>
          <div className="p-4 space-y-2">
            {m.protocol_stats.map((p) => (
              <div key={p.protocol} className="flex items-center gap-3">
                <div className="w-14 text-[10px] font-mono uppercase text-zinc-500">{p.protocol}</div>
                <div className="flex-1 h-2 bg-zinc-900 rounded-sm overflow-hidden">
                  <div className="h-full" style={{ width: pct(p.connections, 8000), background: "var(--tenant-primary)" }} />
                </div>
                <div className="w-16 text-right font-mono text-xs">{p.connections.toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
